import { MoreOutlined, PlusOutlined } from '@ant-design/icons';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  Button,
  Checkbox,
  Divider,
  Dropdown,
  Empty,
  Popconfirm,
  Popover,
  Tag
} from 'antd';
import { debounce, filter, forEach, isEmpty, map } from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../../AppContext';
import FilterSelectedIconComponent from '../../../app/components/iconComponents/FilterSelectedComponent';
import ExportIcon from '../../../assets/export.svg';
import FilterIcon from '../../../assets/filter.svg';
import ImportIcon from '../../../assets/import.svg';
import {
  ROUTES,
  SKIP_RECORD,
  TABLET_BREAKPOINT,
  exportOptions
} from '../../../common/constants';
import {
  checkPermissions,
  handleExportCommon,
  strippedString
} from '../../../common/utils';
import AccessControl from '../../../components/AccessControl';
import LoaderComponent from '../../../components/LoaderComponent';
import Portal from '../../../components/Portal';
import SearchComponent from '../../../components/SearchComponent';
import TableComponent from '../../../components/TableComponent';
import history from '../../../historyData';
import ImportModal from '../../imports/components/ImportModal';
import {
  DELETE_PRODUCT_CATEGORY,
  UPDATE_PRODUCT_CATEGORY
} from '../graphql/Mutations';
import {
  GET_PRODUCT_CATEGORIES,
  PRODUCT_CATEGORY_FILTER
} from '../graphql/Queries';

let scrollDebounce = null;

const ProductCategoryTable = () => {
  const {
    state: { pageSize, filterData, windowWidth, permissions }
  } = useContext(AppContext);

  const initialPaginationValue = {
    total: 0,
    current: 1
  };

  const initialProductCategoryFilter = {
    skip: 0,
    limit: pageSize,
    sortOn: 'createdAt',
    sortBy: 'DESC'
  };

  const [paginationProp, setPaginationProp] = useState(initialPaginationValue);
  const [showImportModal, setShowImportModal] = useState(false);
  const [sortedInfo, setSortedInfo] = useState({});
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [productCategoryFilter, setProductCategoryFilter] = useState(
    initialProductCategoryFilter
  );
  const [filters, setFilters] = useState(filterData);
  const [filtersCopyState, setFiltersCopyState] = useState(filterData);
  const [filterSearch, setFilterSearch] = useState('');
  const [filterLoading, setFilterLoading] = useState(false);
  const [filterList, setFilterList] = useState([]);
  const [scrollFlag, setScrollFlag] = useState(false);
  const [filterVisible, setFilterVisible] = useState(false);
  const [filterIndex, setFilterIndex] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [filterIsEnd, setFilterIsEnd] = useState(false);
  const [searchValue, setSearchValue] = useState(null);

  const [
    updateProductCategoryMutate,
    { loading: updateProductCategoryLoading }
  ] = useMutation(UPDATE_PRODUCT_CATEGORY, {
    onError() {}
  });

  const [
    deleteProductCategory,
    { loading: deleteProductCategoryLoading }
  ] = useMutation(DELETE_PRODUCT_CATEGORY, {
    onError() {}
  });

  const [fetchProductCategoryData, { loading, data }] = useLazyQuery(
    GET_PRODUCT_CATEGORIES,
    {
      fetchPolicy: 'network-only',
      onCompleted: (res) => {
        const pagination = {
          ...paginationProp,
          defaultPageSize: pageSize,
          total: res?.productCategories?.count
        };
        setPaginationProp(pagination);
      },
      onError() {}
    }
  );

  const [productCategoryFilters] = useLazyQuery(PRODUCT_CATEGORY_FILTER, {
    fetchPolicy: 'network-only',
    onCompleted: (res) => {
      if (scrollFlag) {
        const optionsCopy = [...filterList];
        forEach(res?.productCategoryFilters?.data, (item) =>
          optionsCopy?.push(item?.[filterIndex])
        );
        setFilterList(optionsCopy);
        setScrollFlag(false);
      } else {
        const optionsCopy = [];
        forEach(res?.productCategoryFilters?.data, (item) =>
          optionsCopy?.push(item?.[filterIndex])
        );
        setFilterList(optionsCopy);
      }
      setFilterIsEnd(res?.productCategoryFilters?.data?.length < SKIP_RECORD);
      setFilterLoading(false);
    },
    onError: () => {
      setFilterLoading(false);
    }
  });

  useEffect(() => {
    const permitted = checkPermissions(permissions, [
      'FET_PRODUCT_CATEGORY_LIST'
    ]);
    const whereFilter = history?.location?.state;
    let parsedObject;

    if (!isEmpty(whereFilter)) {
      parsedObject = JSON?.parse(whereFilter?.stringifyObject);
    }

    if (permitted) {
      fetchProductCategoryData({
        variables: {
          filter: parsedObject?.productCategoryFilter ?? productCategoryFilter,
          ...(parsedObject && { where: parsedObject?.filters })
        }
      });
      if (!isEmpty(parsedObject)) {
        setFilters({ ...parsedObject?.filters });
        setFiltersCopyState({
          ...parsedObject?.filters
        });

        setSearchValue(parsedObject?.productCategoryFilter?.search);
        const sorter = {
          order:
            parsedObject?.productCategoryFilter?.sortBy === 'ASC'
              ? 'ascend'
              : 'descend',
          columnKey: parsedObject?.productCategoryFilter?.sortOn
        };
        setSortedInfo({ ...sorter });
        setProductCategoryFilter({ ...parsedObject?.productCategoryFilter });
        setPaginationProp({ ...parsedObject?.paginationProp });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // eslint-disable-next-line no-undef
    window?.addEventListener('beforeunload', () => {
      // eslint-disable-next-line no-undef
      if (window?.location?.pathname === ROUTES?.PRODUCT_CATEGORIES)
        // eslint-disable-next-line no-undef
        window?.history?.pushState({}, '', window?.location?.pathname);
    });
    return () => {
      // eslint-disable-next-line no-undef
      window?.removeEventListener('beforeunload', () => {
        // eslint-disable-next-line no-undef
        window?.history?.pushState({}, '', window?.location?.pathname);
      });
    };
  }, []);

  const getFilterData = (confirm) => {
    fetchProductCategoryData({
      variables: {
        filter: { ...productCategoryFilter, skip: 0 },
        ...(filtersCopyState && { where: filtersCopyState })
      }
    });
    setFilters(filtersCopyState);
    setProductCategoryFilter({
      ...productCategoryFilter,
      skip: 0
    });
    setPaginationProp({ ...paginationProp, skip: 0, current: 1 });
    if (confirm) {
      confirm();
    }
  };

  useEffect(() => {
    if (filterVisible) {
      setFilterList([]);
      setFilterLoading(true);
      switch (filterIndex) {
        default:
          productCategoryFilters({
            variables: {
              filter: {
                sortOn: filterIndex,
                sortBy: 'ASC',
                skip: 0,
                limit: 20,
                search: filterSearch,
                distinct: true,
                getDBField: filterIndex
              }
            }
          });
          break;
      }
    }
    if (!filterVisible) {
      setFilterSearch('');
      setFilterIsEnd(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterVisible]);

  const handleShowImportModal = (visible) => {
    setShowImportModal(visible);
  };

  const onScroll = (event, dataIndex) => {
    const { target } = event;
    const { scrollTop, scrollHeight, offsetHeight } = target || {};

    if (scrollDebounce) {
      scrollDebounce?.cancel();
      scrollDebounce = null;
    }
    scrollDebounce = debounce(() => {
      const scrolledToBottom = scrollTop + offsetHeight >= scrollHeight - 5;
      if (
        scrolledToBottom &&
        filterIndex === dataIndex &&
        filterVisible &&
        !filterIsEnd
      ) {
        setScrollFlag(true);

        switch (filterIndex) {
          default:
            productCategoryFilters({
              variables: {
                filter: {
                  sortOn: dataIndex,
                  sortBy: 'ASC',
                  skip: filterList?.length,
                  limit: 20,
                  search: filterSearch,
                  distinct: true,
                  getDBField: dataIndex
                }
              }
            });
            break;
        }
      }
    }, 500);
    scrollDebounce();
  };

  const rowSelection = {
    fixed: 'left',
    columnWidth: 50,
    selectedRowKeys: selectedKeys,
    onChange: (selectedRowKeys) => {
      setSelectedKeys(selectedRowKeys);
    },
    renderCell(checked, record, index, node) {
      return React.cloneElement(node, {
        'aria-label': record?.id
      });
    }
  };

  const handleReset = (clearFilters, dataIndex) => {
    const filtersCopy = {
      ...filters,
      [dataIndex]: []
    };
    setFilters(filtersCopy);
    setFiltersCopyState(filtersCopy);
    fetchProductCategoryData({
      variables: {
        filter: {
          ...productCategoryFilter,
          skip: 0,
          sortOn: 'createdAt',
          sortBy: 'DESC'
        },
        ...(filtersCopy && { where: filtersCopy })
      }
    });
    setProductCategoryFilter({
      ...productCategoryFilter,
      skip: 0
    });
    setPaginationProp({ ...paginationProp, skip: 0, current: 1 });
    clearFilters();
    setFilterIndex(dataIndex);
    setFilterVisible(false);
  };

  const handleAddEditProductCategory = (record) => {
    const otherFilters = { productCategoryFilter, paginationProp, filters };
    const stringifyObject = JSON?.stringify(otherFilters);
    if (record?.id) {
      history?.push(`${ROUTES?.PRODUCT_CATEGORIES}/edit/${record?.id}`, {
        stringifyObject
      });
    } else {
      history?.push(`${ROUTES?.PRODUCT_CATEGORIES}/add`, {
        stringifyObject
      });
    }
  };

  const importCallback = () => {
    setPaginationProp(initialPaginationValue);
    setProductCategoryFilter(initialProductCategoryFilter);
    setSortedInfo({});
    fetchProductCategoryData({
      variables: { filter: initialProductCategoryFilter }
    });
  };

  const changeFilter = (e, dataIndex) => {
    const {
      target: { value = '' }
    } = e;
    let filtersCopy = [];
    if (filtersCopyState?.[dataIndex]?.includes(value)) {
      filtersCopy = {
        ...filtersCopyState,
        [dataIndex]: filter(
          filtersCopyState?.[dataIndex],
          (item) => item !== value
        )
      };
    } else {
      filtersCopy = {
        ...filtersCopyState,
        [dataIndex]: filtersCopyState?.[dataIndex]
          ? [...filtersCopyState?.[dataIndex], value]
          : [value]
      };
    }
    setFiltersCopyState(filtersCopy);
  };

  const handleTableChange = (pagination, tableFilter, sorter) => {
    const { current } = pagination;
    const skip = (current - 1) * pagination?.pageSize;
    setSortedInfo(sorter);
    setPaginationProp({ ...paginationProp, ...pagination });
    if (sorter?.column) {
      setProductCategoryFilter({
        ...productCategoryFilter,
        skip,
        limit: pagination?.pageSize,
        sortOn: sorter?.field,
        sortBy: sorter?.order === 'ascend' ? 'ASC' : 'DESC'
      });
      fetchProductCategoryData({
        variables: {
          filter: {
            ...productCategoryFilter,
            skip,
            limit: pagination?.pageSize,
            sortOn: sorter?.field,
            sortBy: sorter?.order === 'ascend' ? 'ASC' : 'DESC'
          },
          ...(filters && { where: filters })
        }
      });
    } else {
      setProductCategoryFilter({
        ...productCategoryFilter,
        skip,
        limit: pagination?.pageSize,
        sortOn: 'createdAt',
        sortBy: 'DESC'
      });
      fetchProductCategoryData({
        variables: {
          filter: {
            ...productCategoryFilter,
            skip,
            limit: pagination?.pageSize,
            sortOn: 'createdAt',
            sortBy: 'DESC'
          },
          ...(filters && { where: filters })
        }
      });
    }
  };

  const handleSearch = (value, dataIndex) => {
    setFilterSearch(value);
    setScrollFlag(false);
    switch (dataIndex) {
      default:
        productCategoryFilters({
          variables: {
            filter: {
              sortOn: dataIndex,
              sortBy: 'ASC',
              skip: 0,
              limit: 20,
              search: value,
              distinct: true,
              getDBField: dataIndex
            }
          }
        });
        break;
    }
  };

  const handleDeleteFilter = (value, dataIndex) => {
    const filtersCopy = {
      ...filtersCopyState,
      [dataIndex]: filter(
        filtersCopyState?.[dataIndex],
        (item) => item !== value
      )
    };
    setFiltersCopyState(filtersCopy);
  };

  const filterPopup = (dataIndex) => ({
    filterDropdown: ({ confirm, clearFilters }) => (
      <div className="custom-filter-dropdown">
        <LoaderComponent spinning={filterLoading} setHeight={35}>
          {dataIndex !== 'isActive' && (
            <SearchComponent
              className="list-search-box filter-search"
              id="search-container-id-roles"
              placeholder="Search..."
              name={dataIndex}
              getData={(value) => handleSearch(value, dataIndex)}
            />
          )}
          {filtersCopyState?.[dataIndex]?.length > 0 && (
            <div className="filter-section">
              {map(filtersCopyState?.[dataIndex], (item) => {
                return (
                  <Tag
                    key={item?.toString()}
                    closable
                    onClose={() => handleDeleteFilter(item, dataIndex)}
                    className="filter-tag"
                  >
                    {dataIndex === 'isActive' ? (
                      <span title={item === true ? 'Active' : 'Inactive'}>
                        {item === true ? 'Active' : 'Inactive'}
                      </span>
                    ) : (
                      <span title={item?.label || item?.toString()}>
                        {item?.label || item?.toString()}
                      </span>
                    )}
                  </Tag>
                );
              })}
            </div>
          )}
          <div
            className="filter-checkboxes"
            onScroll={(e) => onScroll(e, dataIndex)}
          >
            {filterList?.length > 0 ? (
              map(filterList, (item) => {
                return (
                  <div
                    className="filter-checkbox-section"
                    key={item?.key || item}
                  >
                    <Checkbox
                      value={item?.key || item}
                      checked={filtersCopyState?.[dataIndex]?.includes(
                        item?.key || item
                      )}
                      key={item?.key || item}
                      onChange={(e) => changeFilter(e, dataIndex)}
                      className="common-checkbox"
                    >
                      {dataIndex === 'isActive' ? (
                        <span title={item === true ? 'Active' : 'Inactive'}>
                          {item === true ? 'Active' : 'Inactive'}
                        </span>
                      ) : (
                        <span title={item?.label || item?.toString()}>
                          {item?.label || item?.toString()}
                        </span>
                      )}
                    </Checkbox>
                  </div>
                );
              })
            ) : (
              <Empty image={Empty?.PRESENTED_IMAGE_SIMPLE} />
            )}
          </div>
        </LoaderComponent>
        <Divider className="divider-filter" />
        <div className="d-flex justify-center">
          <Button
            size="small"
            className="common-button discard-button filter-button"
            id="roles-filter-reset"
            onClick={() => handleReset(clearFilters, dataIndex)}
          >
            Reset
          </Button>
          <Button
            size="small"
            className="common-button filter-button"
            id="roles-filter-ok"
            type="primary"
            onClick={() => getFilterData(confirm, dataIndex)}
          >
            Ok
          </Button>
        </div>
      </div>
    ),
    filterIcon: () =>
      filters?.[dataIndex]?.length > 0 ? (
        <FilterSelectedIconComponent className="primary-color" />
      ) : (
        <img src={FilterIcon} alt="filter-icon" width={16} />
      ),
    onFilterDropdownOpenChange: (visible) => {
      setFilterIndex(dataIndex);
      setFilterVisible(visible);
    }
  });

  const handleDeleteProductCategory = async (editProductCategoryData) => {
    const response = await deleteProductCategory({
      variables: { where: { id: editProductCategoryData?.id } }
    });
    if (response?.data?.deleteProductCategory) {
      fetchProductCategoryData({
        variables: {
          filter: productCategoryFilter,
          ...(filters && { where: filters })
        }
      });
    }
  };

  const handleProductCategoryStatus = async (editProductCategoryData) => {
    const editData = {
      ...editProductCategoryData,
      isActive: !editProductCategoryData?.isActive
    };
    // eslint-disable-next-line no-underscore-dangle
    delete editData?.__typename;
    delete editData?.id;
    delete editData?.productCategoryCode;
    delete editData?.description;
    delete editData?.tenantId;
    const response = await updateProductCategoryMutate({
      variables: {
        data: { ...editData },
        where: { id: editProductCategoryData?.id }
      }
    });
    if (response?.data?.updateProductCategory) {
      fetchProductCategoryData({
        variables: {
          filter: productCategoryFilter,
          ...(filters && { where: filters })
        }
      });
    }
  };

  const renderActionButtons = (editProductCategoryData) => {
    return (
      <div className="d-flex flex-vertical">
        <AccessControl
          allowedPermissions={[
            'FET_PRODUCT_CATEGORY_UPDATE',
            'FET_PRODUCT_CATEGORY_VIEW'
          ]}
        >
          <Button
            id="productCategory-table-edit-btn"
            className="b-0"
            onClick={() =>
              handleAddEditProductCategory(editProductCategoryData)
            }
          >
            Edit
          </Button>
        </AccessControl>
        <AccessControl allowedPermissions={['FET_PRODUCT_CATEGORY_DELETE']}>
          <Popconfirm
            title="Are you sure to delete?"
            onConfirm={() =>
              handleDeleteProductCategory(editProductCategoryData)
            }
            okText="Yes"
            cancelText="No"
          >
            <Button id="productCategory-table-status-btn" className="b-0">
              Delete
            </Button>
          </Popconfirm>
        </AccessControl>
        <AccessControl allowedPermissions={['FET_PRODUCT_CATEGORY_UPDATE']}>
          <Popconfirm
            title={`Are you sure to ${
              editProductCategoryData?.isActive
                ? 'Mark Inactive'
                : 'Mark Active'
            }?`}
            onConfirm={() =>
              handleProductCategoryStatus(editProductCategoryData)
            }
            okText="Yes"
            cancelText="No"
          >
            <Button id="productCategory-table-status-btn" className="b-0">
              {editProductCategoryData?.isActive
                ? 'Mark Inactive'
                : 'Mark Active'}
            </Button>
          </Popconfirm>
        </AccessControl>
      </div>
    );
  };

  const columns = [
    {
      title: 'TITLE',
      dataIndex: 'title',
      key: 'title',
      sorter: true,
      ellipsis: true,
      sortOrder: sortedInfo?.columnKey === 'title' && sortedInfo?.order,
      width: 120,
      className: 'max-width-column'
    },
    {
      title: 'DESCRIPTION',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      className: 'max-width-column',
      render: (description = '') => {
        return strippedString(description) || '-';
      }
    },
    {
      title: 'STATUS',
      dataIndex: 'isActive',
      key: 'isActive',
      sorter: true,
      ellipsis: true,
      sortOrder: sortedInfo?.columnKey === 'isActive' && sortedInfo?.order,
      width: windowWidth < TABLET_BREAKPOINT ? 120 : 120,
      ...filterPopup('isActive'),
      render: (isActive) => {
        if (isActive) {
          return <span>Active</span>;
        }

        return <span>Inactive</span>;
      }
    },
    checkPermissions(permissions, [
      'FET_PRODUCT_CATEGORY_UPDATE',
      'FET_PRODUCT_CATEGORY_DELETE',
      'FET_PRODUCT_CATEGORY_VIEW'
    ]) && {
      dataIndex: 'id',
      align: 'right',
      width: 30,
      fixed: 'right',
      render: (id, record) => {
        return (
          <Popover
            placement="bottom"
            overlayClassName="action-button"
            content={renderActionButtons(record)}
          >
            <MoreOutlined />
          </Popover>
        );
      }
    }
  ];

  const onSearchChange = (value) => {
    setProductCategoryFilter({
      ...productCategoryFilter,
      skip: value
        ? 0
        : productCategoryFilter?.limit * (paginationProp?.current - 1),
      search: value
    });
    fetchProductCategoryData({
      variables: {
        filter: {
          ...productCategoryFilter,
          skip: value
            ? 0
            : productCategoryFilter?.limit * (paginationProp?.current - 1),
          search: value
        },
        ...(filters && { where: filters })
      }
    });
  };

  const handleExport = async (e) => {
    const { key } = e;
    setExportLoading(true);
    await handleExportCommon({
      filters,
      key,
      selectedKeys,
      module: 'PRODUCT_CATEGORY'
    });
    setExportLoading(false);
  };

  return (
    <div>
      <ImportModal
        showImportModal={showImportModal}
        setShowImportModal={setShowImportModal}
        callback={importCallback}
        module="product-categories"
        folder="PRODUCT_CATEGORY"
      />
      <AccessControl allowedPermissions={['FET_PRODUCT_CATEGORY_CREATE']}>
        <Portal portalId="header-right-content">
          <Button
            className="common-button"
            icon={<PlusOutlined />}
            size="small"
            id="productCategory-table-add-btn"
            type="primary"
            onClick={handleAddEditProductCategory}
          >
            Add Category
          </Button>
        </Portal>
      </AccessControl>
      <div className="mb-16 d-flex justify-between align-center">
        <div className="fill-width search-checkbox">
          <AccessControl allowedPermissions={['FET_PRODUCT_CATEGORY_LIST']}>
            <SearchComponent
              className="list-search-box"
              id="search-container-id"
              placeholder="Category name or other detail.."
              name="ProductCategories"
              getData={onSearchChange}
              value={searchValue}
              setSearchValue={setSearchValue}
            />
          </AccessControl>
        </div>
        <div className="header-buttons">
          <AccessControl allowedPermissions={['FET_PRODUCT_CATEGORY_IMPORT']}>
            <Button
              size="small"
              className="common-button import-button"
              icon={<img src={ImportIcon} alt="import-icon" width={11} />}
              id="productCategory-table-import-btn"
              type="primary"
              onClick={() => handleShowImportModal(true)}
            >
              Import
            </Button>
          </AccessControl>
          <AccessControl allowedPermissions={['FET_PRODUCT_CATEGORY_EXPORT']}>
            <Dropdown
              overlayClassName="export-btn-dropdown"
              menu={{ items: exportOptions, onClick: handleExport }}
              placement="bottom"
            >
              <Button
                className="common-button export-button"
                size="small"
                icon={<img src={ExportIcon} alt="export-icon" width={11} />}
                id="productCategory-table-export-btn"
                loading={exportLoading}
                type="primary"
              >
                Export
              </Button>
            </Dropdown>
          </AccessControl>
        </div>
      </div>
      <AccessControl
        allowedPermissions={['FET_PRODUCT_CATEGORY_LIST']}
        showNoAccess
      >
        <div className="common-table">
          {pageSize && (
            <TableComponent
              loadingData={
                loading ||
                updateProductCategoryLoading ||
                deleteProductCategoryLoading
              }
              columns={[...columns?.filter((item) => item !== false)]}
              data={data?.productCategories?.data || []}
              onChange={handleTableChange}
              paginationConfig={paginationProp}
              rowSelection={rowSelection}
              rowKey={(obj) => obj?.id}
            />
          )}
        </div>
      </AccessControl>
    </div>
  );
};

export default ProductCategoryTable;
